import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import MuxPlayer from '@mux/mux-player-react';
import { Star, X, Flag, Video, Plus, Loader2, AlertTriangle } from 'lucide-react';
import VideoReviewRecorder from './VideoReviewRecorder';

const VideoReviewBubbles = ({ contentId }) => {
  const { user, isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [flagging, setFlagging] = useState(null);

  useEffect(() => {
    loadReviews();
  }, [contentId]);

  const loadReviews = async () => {
    try {
      const response = await api.get(`/video-reviews/${contentId}`);
      setReviews(response.data);
    } catch (err) {
      console.error('Failed to load video reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFlag = async (reviewId) => {
    if (!isAuthenticated) {
      alert('Please sign in to flag reviews');
      return;
    }
    
    setFlagging(reviewId);
    try {
      const response = await api.post(`/video-reviews/${reviewId}/flag`);
      if (response.data.removed) {
        // Review was removed, refresh list
        setReviews(reviews.filter(r => r.id !== reviewId));
        setSelectedReview(null);
      }
      alert(response.data.message);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to flag review');
    } finally {
      setFlagging(null);
    }
  };

  const canPostReview = user?.is_premium && user?.can_post_video_reviews !== false;

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-foreground/40">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading reviews...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Video className="h-5 w-5 text-electric" />
          <span>Video Reviews</span>
          {reviews.length > 0 && (
            <span className="text-sm font-normal text-foreground/60">({reviews.length})</span>
          )}
        </h3>
        
        {canPostReview && (
          <button
            onClick={() => setShowRecorder(true)}
            className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-electric text-background font-medium text-sm hover:bg-electric/90 transition-colors"
            data-testid="add-video-review-btn"
          >
            <Plus className="h-4 w-4" />
            <span>Add Review</span>
          </button>
        )}
        
        {!canPostReview && isAuthenticated && !user?.is_premium && (
          <span className="text-xs text-foreground/40 bg-white/5 px-3 py-1 rounded-full">
            Premium required to post
          </span>
        )}
      </div>

      {/* Review Bubbles */}
      {reviews.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {reviews.map((review) => (
            <button
              key={review.id}
              onClick={() => setSelectedReview(review)}
              className="group relative"
              data-testid={`video-review-bubble-${review.id}`}
            >
              {/* Circular thumbnail */}
              <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/20 hover:border-electric transition-colors">
                {review.thumbnail_url ? (
                  <img
                    src={review.thumbnail_url}
                    alt={`${review.username}'s review`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-white/10 flex items-center justify-center">
                    <Video className="h-6 w-6 text-foreground/40" />
                  </div>
                )}
              </div>
              
              {/* Star rating badge */}
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 flex items-center space-x-0.5 px-1.5 py-0.5 rounded-full bg-background border border-white/20">
                <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                <span className="text-xs text-foreground font-medium">{review.star_rating}</span>
              </div>
              
              {/* Username tooltip on hover */}
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 rounded bg-black/80 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                {review.username}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-white/5 rounded-xl">
          <Video className="h-10 w-10 text-foreground/20 mx-auto mb-2" />
          <p className="text-foreground/40 text-sm">No video reviews yet</p>
          {canPostReview && (
            <button
              onClick={() => setShowRecorder(true)}
              className="mt-3 text-electric text-sm hover:underline"
            >
              Be the first to review!
            </button>
          )}
        </div>
      )}

      {/* Review Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10">
                  {selectedReview.avatar ? (
                    <img src={selectedReview.avatar} alt={selectedReview.username} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground/40">
                      {selectedReview.username[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-foreground font-medium">{selectedReview.username}</p>
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        className={`h-3 w-3 ${star <= selectedReview.star_rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setSelectedReview(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-foreground/60" />
              </button>
            </div>

            {/* Video */}
            <div className="aspect-square bg-black">
              {selectedReview.mux_playback_id ? (
                <MuxPlayer
                  playbackId={selectedReview.mux_playback_id}
                  autoPlay
                  style={{ width: '100%', height: '100%' }}
                  primaryColor="#00F5D4"
                />
              ) : (
                <video
                  src={selectedReview.video_url}
                  autoPlay
                  controls
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Actions */}
            <div className="p-4 flex items-center justify-between">
              <span className="text-xs text-foreground/40">
                {new Date(selectedReview.created_at).toLocaleDateString()}
              </span>
              
              {isAuthenticated && selectedReview.user_id !== user?.id && (
                <button
                  onClick={() => handleFlag(selectedReview.id)}
                  disabled={flagging === selectedReview.id}
                  className="flex items-center space-x-1 px-3 py-1.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors text-sm disabled:opacity-50"
                >
                  {flagging === selectedReview.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Flag className="h-4 w-4" />
                  )}
                  <span>Flag</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recorder Modal */}
      {showRecorder && (
        <VideoReviewRecorder
          contentId={contentId}
          onClose={() => setShowRecorder(false)}
          onSuccess={() => {
            setShowRecorder(false);
            loadReviews();
          }}
        />
      )}
    </div>
  );
};

export default VideoReviewBubbles;
